import {
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faQuestionCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';

import type {
  ZuubCoverage,
  ZuubPatientData,
  ZuubPatientDetailedData,
  ZuubService,
} from '../../@types/zuub/contracts';
import type { Appointment } from '../../react-query/useAppointments';

import { getMockAppointmentInsuranceStatus } from './mockData';

/**
 * Determines if the View Benefits button should be disabled based on patient data
 *
 * @param zuubPatient - The Zuub patient data
 * @param patientId - The patient ID
 * @returns True if the button should be disabled, false otherwise
 */
export const shouldDisableViewBenefitsButton = (
  zuubPatient: ZuubPatientData | undefined,
  patientId: string | undefined,
): boolean => {
  if (!patientId) return true;

  const verificationStatus = zuubPatient?.verificationStatus?.toLowerCase();
  const insuranceStatus = zuubPatient?.insuranceStatus?.toLowerCase();
  const payerName = zuubPatient?.payerName?.toLowerCase();

  // Check for conditions that should disable the button
  const isPayerError =
    insuranceStatus === 'inactive' || payerName === 'unknown payer';
  const isPatientNotFound = !zuubPatient;
  const isMissingData =
    verificationStatus === 'missing data' ||
    verificationStatus === 'patient not found';

  return isPayerError || isPatientNotFound || isMissingData;
};

/**
 * Creates a map of unifiedAppointmentId to Zuub patient data for quick lookup
 * This improves performance from O(n) to O(1) when looking up Zuub data for each appointment
 *
 * @param zuubData - The Zuub API response data
 * @returns A Map with unifiedAppointmentId as key and ZuubPatientData as value
 */
export const createZuubDataMap = (
  zuubData: { patients: ZuubPatientData[] } | undefined,
): Map<string, ZuubPatientData> => {
  if (!zuubData?.patients) return new Map();

  const map = new Map<string, ZuubPatientData>();
  zuubData.patients.forEach((zuubPatient) => {
    map.set(zuubPatient.unifiedAppointmentId, zuubPatient);
  });
  return map;
};

/**
 * Sorts appointments based on the specified field and direction
 *
 * @param appointments - Array of appointments to sort
 * @param sortField - The field to sort by ('flag', 'patient', 'appointment', 'procedureCodes')
 * @param sortDirection - The sort direction ('asc' or 'desc')
 * @param zuubDataMap - Optional map of unifiedAppointmentId to ZuubPatientData for flag sorting
 * @returns Sorted array of appointments
 */
export const sortAppointments = (
  appointments: Appointment[],
  sortField: string,
  sortDirection: 'asc' | 'desc',
  zuubDataMap?: Map<string, ZuubPatientData>,
): Appointment[] => {
  if (!appointments) return [];

  return appointments.sort((a, b) => {
    if (sortField === 'flag') {
      if (!zuubDataMap) return 0;

      const aZuubPatient = zuubDataMap.get(a.appointment.unifiedId);
      const bZuubPatient = zuubDataMap.get(b.appointment.unifiedId);

      const hasFlagA =
        aZuubPatient?.detailedData?.some(
          (data) => data.needsPayerPortalConnection,
        ) ?? false;
      const hasFlagB =
        bZuubPatient?.detailedData?.some(
          (data) => data.needsPayerPortalConnection,
        ) ?? false;

      // In ascending order: items with flags first (true = -1, false = 1)
      // In descending order: items with flags last (true = 1, false = -1)
      if (hasFlagA !== hasFlagB) {
        return sortDirection === 'asc'
          ? hasFlagA
            ? -1
            : 1
          : hasFlagA
          ? 1
          : -1;
      }

      // If both have the same flag status, maintain original order
      return 0;
    }
    if (sortField === 'patient') {
      const aName = a.patient.firstName + ' ' + a.patient.lastName;
      const bName = b.patient.firstName + ' ' + b.patient.lastName;
      return sortDirection === 'asc'
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    }
    if (sortField === 'appointment') {
      const aDate = new Date(a.appointment.start).getTime();
      const bDate = new Date(b.appointment.start).getTime();
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }
    if (sortField === 'procedureCodes') {
      // For now, procedure codes are empty, so no sorting needed
      return 0;
    }
    return 0;
  });
};

/**
 * Gets the display configuration for verification status
 *
 * @param status - The verification status string
 * @returns Object with status text, icon, and icon color
 */
export const getVerificationStatusDisplay = (status: string) => {
  switch (status.toLowerCase()) {
    case 'verified':
      return {
        status: 'Verified',
        icon: faCheckCircle,
        iconColor: 'vai-text-green-600',
      };
    case 'failed':
      return {
        status: 'Failed',
        icon: faTimesCircle,
        iconColor: 'vai-text-red-600',
      };
    case 'missing data':
    case 'missing_data':
      return {
        status: 'Missing data',
        icon: faQuestionCircle,
        iconColor: 'vai-text-amber-500',
      };
    case 'needs attention':
    case 'needs_attention':
      return {
        status: 'Needs attention',
        icon: faExclamationTriangle,
        iconColor: 'vai-text-amber-500',
      };
    case 'pending':
      return {
        status: 'Pending',
        icon: faClock,
      };
    default:
      return {
        status: status || 'Unknown',
        icon: faQuestionCircle,
        iconColor: 'vai-text-gray-500',
      };
  }
};

/**
 * Gets the insurance status configuration for StatusChip
 *
 * @param status - The insurance status string
 * @returns Object with variant and optional icon
 */
export const getInsuranceStatusConfig = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return {
        variant: 'success' as const,
        icon: undefined,
      };
    case 'inactive':
      return {
        variant: 'error' as const,
        icon: undefined,
      };
    case 'pending':
      return {
        variant: 'default' as const,
        icon: faClock,
      };
    case 'unknown':
      return {
        variant: 'warning' as const,
        icon: undefined,
      };
    default:
      return {
        variant: 'default' as const,
        icon: undefined,
      };
  }
};

/**
 * Gets the verification status for an appointment
 * Checks Zuub data first, then falls back to a default status
 *
 * @param appointment - The appointment to get verification status for
 * @param zuubDataMap - Map of unifiedAppointmentId to ZuubPatientData
 * @returns The verification status string (lowercase)
 */
export const getAppointmentVerificationStatus = (
  appointment: Appointment,
  zuubDataMap: Map<string, ZuubPatientData>,
): string => {
  const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
  const verificationStatus = zuubPatient?.verificationStatus
    ? getVerificationStatusDisplay(zuubPatient.verificationStatus)
    : { status: 'Unknown' }; // Default status when no Zuub data
  return verificationStatus.status.toLowerCase();
};

/**
 * Gets the insurance status for an appointment
 * Checks Zuub data first, then falls back to mock data
 *
 * @param appointment - The appointment to get insurance status for
 * @param zuubDataMap - Map of unifiedAppointmentId to ZuubPatientData
 * @returns The insurance status string (lowercase)
 */
export const getAppointmentInsuranceStatus = (
  appointment: Appointment,
  zuubDataMap: Map<string, ZuubPatientData>,
): string => {
  const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
  const status =
    zuubPatient?.insuranceStatus ||
    getMockAppointmentInsuranceStatus(appointment.appointment.unifiedId);
  return status.toLowerCase();
};

/**
 * Extracts unique payer names from Zuub patient data
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @returns Array of unique payer names
 */
export const getUniquePayerNames = (
  zuubPatientData: ZuubPatientData | null,
): string[] => {
  if (!zuubPatientData?.detailedData) return [];

  const payerNames = zuubPatientData.detailedData
    .map((plan) => plan.plan.payerName)
    .filter((name): name is string => !!name);

  return [...new Set(payerNames)]; // Remove duplicates
};

/**
 * Extracts unique plan types from Zuub patient data
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @returns Array of unique plan types
 */
export const getUniquePlanTypes = (
  zuubPatientData: ZuubPatientData | null,
): string[] => {
  if (!zuubPatientData?.detailedData) return [];

  const planTypes = zuubPatientData.detailedData
    .map((plan) => plan.plan.planType)
    .filter((type): type is string => !!type);

  return [...new Set(planTypes)]; // Remove duplicates
};

/**
 * Gets plan types available for a specific payer name
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @param payerName - The payer name to filter by
 * @returns Array of plan types available for the specified payer
 */
export const getPlanTypesForPayer = (
  zuubPatientData: ZuubPatientData | null,
  payerName: string,
): string[] => {
  if (!zuubPatientData?.detailedData || !payerName) return [];

  const planTypes = zuubPatientData.detailedData
    .filter((plan) => plan.plan.payerName === payerName)
    .map((plan) => plan.plan.planType)
    .filter((type): type is string => !!type);

  return [...new Set(planTypes)]; // Remove duplicates
};

/**
 * Finds a plan by payer name and returns its status
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @param payerName - The payer name to search for
 * @returns The plan status (active/inactive) or null if not found
 */
export const getPlanStatusByPayerName = (
  zuubPatientData: ZuubPatientData | null,
  payerName: string,
): string | null => {
  if (!zuubPatientData?.detailedData) return null;

  const plan = zuubPatientData.detailedData.find(
    (planData) => planData.plan.payerName === payerName,
  );

  return plan?.plan?.status || null;
};

/**
 * Finds a plan by plan type and returns its status
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @param planType - The plan type to search for
 * @returns The plan status (active/inactive) or null if not found
 */
export const getPlanStatusByPlanType = (
  zuubPatientData: ZuubPatientData | null,
  planType: string,
): string | null => {
  if (!zuubPatientData?.detailedData) return null;

  const plan = zuubPatientData.detailedData.find(
    (planData) => planData.plan.planType === planType,
  );

  return plan?.plan?.status || null;
};

/**
 * Checks if a plan status indicates the plan is active
 *
 * @param status - The plan status string
 * @returns True if the plan is active, false otherwise
 */
export const isPlanActive = (status: string | null): boolean => {
  return status?.toLowerCase() === 'active';
};

/**
 * Gets benefits data for a specific coverage category
 */
export const getBenefitsByCategory = (
  zuubPatientData: ZuubPatientData | null,
  categoryName: string,
): { remaining: number; total: number } => {
  if (!zuubPatientData?.detailedData?.[0]) {
    return { remaining: 0, total: 0 };
  }

  const plan = zuubPatientData.detailedData[0];
  const category = plan.coverage?.find(
    (coverage) =>
      coverage.categoryName.toLowerCase() === categoryName.toLowerCase(),
  );

  if (!category) {
    return { remaining: 0, total: 0 };
  }

  // Use the overall benefit summary for total benefits
  // and calculate remaining benefits from individual services in this category
  const total = plan.benefitSummary?.totalBenefits || 0;
  let remaining = 0;

  category.services.forEach((service: ZuubService) => {
    const serviceRemaining = parseFloat(service.remainingBenefits) || 0;
    remaining += serviceRemaining;
  });

  return { remaining, total };
};

/**
 * Gets benefits data for a specific coverage category from a single plan
 */
export const getBenefitsByCategoryFromPlan = (
  plan: ZuubPatientDetailedData | null,
  categoryName: string,
): { remaining: number; total: number } => {
  if (!plan?.coverage) {
    return { remaining: 0, total: 0 };
  }

  const category = plan.coverage.find(
    (coverage: ZuubCoverage) =>
      coverage.categoryName.toLowerCase() === categoryName.toLowerCase(),
  );

  if (!category) {
    return { remaining: 0, total: 0 };
  }

  // Use the overall benefit summary for total benefits
  // and calculate remaining benefits from individual services in this category
  const total = plan.benefitSummary?.totalBenefits || 0;
  let remaining = 0;

  category.services.forEach((service: ZuubService) => {
    const serviceRemaining = parseFloat(service.remainingBenefits) || 0;
    remaining += serviceRemaining;
  });

  return { remaining, total };
};

/**
 * Gets the selected plan based on payer name and plan type
 * If planType is null, returns the first plan with the matching payer name
 */
export const getSelectedPlan = (
  zuubPatientData: ZuubPatientData | null,
  payerName: string,
  planType: string | null,
) => {
  if (!zuubPatientData?.detailedData) {
    return null;
  }

  // If planType is null, just find the first plan with the matching payer name
  if (!planType) {
    return (
      zuubPatientData.detailedData.find(
        (plan) => plan.plan.payerName === payerName,
      ) || null
    );
  }

  // Otherwise, find the plan that matches both payer name and plan type
  const exactMatch = zuubPatientData.detailedData.find(
    (plan) =>
      plan.plan.payerName === payerName && plan.plan.planType === planType,
  );

  // If no exact match found, fall back to the first plan with the matching payer name
  if (!exactMatch) {
    return (
      zuubPatientData.detailedData.find(
        (plan) => plan.plan.payerName === payerName,
      ) || null
    );
  }

  return exactMatch;
};

/**
 * Calculates summary metrics and filters appointments for table display
 * This function processes appointments to:
 * 1. Count verification statuses for SummaryCards (pending, failed, verified)
 * 2. Filter appointments for the table based on the selected card
 *
 * @param appointments - Array of appointments to process
 * @param selectedCard - The currently selected card filter ('all', 'pending', 'failed', 'verified')
 * @param zuubDataMap - Map of unifiedAppointmentId to ZuubPatientData for status lookup
 * @returns Object containing summaryMetrics and filteredAppointmentsForTable
 */
export const calculateSummaryMetricsAndFilterAppointments = (
  appointments: Appointment[] | undefined,
  selectedCard: string,
  zuubDataMap: Map<string, ZuubPatientData>,
): {
  summaryMetrics: {
    total: number;
    pending: number;
    failed: number;
    verified: number;
  };
  filteredAppointmentsForTable: Appointment[];
} => {
  if (!appointments) {
    return {
      summaryMetrics: { total: 0, pending: 0, failed: 0, verified: 0 },
      filteredAppointmentsForTable: [],
    };
  }

  const result = appointments.reduce(
    (acc, appointment) => {
      // Get the verification status for this appointment
      // This checks Zuub data first, then falls back to 'unknown' status
      const status = getAppointmentVerificationStatus(appointment, zuubDataMap);

      // Count verification statuses for SummaryCards
      // These counts will be displayed on the cards (e.g., "3 Pending", "1 Failed")
      if (status === 'pending') acc.metrics.pending++;
      else if (status === 'failed') acc.metrics.failed++;
      else if (status === 'verified') acc.metrics.verified++;

      // Filter appointments for the table based on selected card
      // If "All" is selected, include all appointments
      // If a specific status is selected (e.g., "Pending"), only include those
      if (selectedCard === 'all' || status === selectedCard) {
        acc.filteredForTable.push(appointment);
      }

      return acc;
    },
    {
      metrics: {
        total: appointments.length,
        pending: 0,
        failed: 0,
        verified: 0,
      },
      filteredForTable: [] as Appointment[],
    },
  );

  return {
    summaryMetrics: result.metrics, // For SummaryCards display
    filteredAppointmentsForTable: result.filteredForTable, // For table display
  };
};
